/**
 * <stat-counters>
 *
 * Big numbers that count up from 0 when they scroll into view. Respects prefers-reduced-motion
 * (shows the final value immediately). Values are formatted with thousands separators.
 */
const SC_REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

class StatCounters extends HTMLElement {
  #io = null;

  connectedCallback() {
    this.nums = Array.from(this.querySelectorAll('[data-sc-num]'));
    if (SC_REDUCE.matches) {
      this.nums.forEach((n) => this.#set(n, this.#target(n)));
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#run(entry.target);
            this.#io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    this.nums.forEach((n) => {
      this.#set(n, 0);
      this.#io.observe(n);
    });

    // Safety net: if the observer never fires, numbers must not sit at 0 forever.
    setTimeout(() => {
      this.nums.forEach((n) => {
        if (!n.dataset.scDone) this.#set(n, this.#target(n));
      });
    }, 4000);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  #target(n) {
    return parseFloat(n.dataset.scNum) || 0;
  }

  #set(n, val) {
    const dec = parseInt(n.dataset.scDecimals || '0', 10) || 0;
    const valEl = n.querySelector('[data-sc-val]');
    if (valEl) {
      valEl.textContent = Number(val).toLocaleString(undefined, {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      });
    }
  }

  #run(n) {
    n.dataset.scDone = '1';
    const target = this.#target(n);
    const duration = 1500;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      this.#set(n, target * eased);
      if (t < 1) requestAnimationFrame(tick);
      else this.#set(n, target);
    };
    requestAnimationFrame(tick);
  }
}

if (!customElements.get('stat-counters')) {
  customElements.define('stat-counters', StatCounters);
}
